import React from "react"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { Input } from "@/components/input"
import { Separator } from "@/components/separator"
import { MessageBox } from "@/domains/common/components/message-box"
import { MessageBoxIcon } from "@/domains/common/components/message-box-icon"
import { BodySM } from "@/domains/design/components/body-sm"
import { cn } from "@/domains/library"
import { TransferExpiredPanel } from "@/domains/lock-page/components/card/transfer-card/composables/transfer-expired-panel/transfer-expired-panel"
import { TransferVotedPanel } from "@/domains/lock-page/components/card/transfer-card/composables/transfer-voted-panel/transfer-voted-panel"

export interface TransferCardProps {
  nftId: number
  destAddress: string
  isValidAddress: boolean
  shouldDisplayError: boolean
  hasExpired: boolean
  isReset: boolean
  canTransfer: boolean
  hasVotedThisEpoch: boolean
  icon?: React.ReactElement
  onAddressChange: (address: string) => void
  onTransfer: () => void
}

export const TransferCard = ({
  nftId,
  destAddress,
  isValidAddress,
  shouldDisplayError,
  hasExpired,
  isReset,
  canTransfer,
  hasVotedThisEpoch,
  icon,
  onAddressChange,
  onTransfer,
}: TransferCardProps) => {
  return (
    <Card.Container
      className={cn("flex w-full flex-col gap-6", "rounded-lg border border-p400-light bg-p400 p-4 lg:p-6")}
    >
      <Card.Header>
        {icon && (
          <Card.HeaderIconContainer>
            {React.cloneElement(icon, { className: "text-t200", size: 24 })}
          </Card.HeaderIconContainer>
        )}

        <div className="flex w-full flex-col">
          <Card.HeaderTitle>Transfer</Card.HeaderTitle>
          <Card.HeaderDescription>Transfer #{nftId} to another address.</Card.HeaderDescription>
        </div>
      </Card.Header>
      <Separator
        className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
  after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
  after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
        decorative
      />

      {hasExpired && !isReset ? (
        <TransferExpiredPanel isReset={isReset} />
      ) : (
        <>
          <ShowWhenTrue when={canTransfer || (hasExpired && isReset)}>
            <div className="flex flex-col gap-1">
              <p className="text-sm text-t100">Destination Address</p>
              <Input
                placeholder="0x..."
                value={destAddress}
                className={cn(
                  "bg-p500",
                  shouldDisplayError && "ring-error/50 focus-visible:ring-error/50 ring-2 focus-visible:ring-2 ",
                )}
                onChange={(e) => onAddressChange(e.target.value)}
                type="text"
              />
              {shouldDisplayError && <p className="text-xs text-error-text">Invalid address.</p>}
            </div>
          </ShowWhenTrue>

          <ShowWhenTrue when={!canTransfer && !hasExpired && hasVotedThisEpoch}>
            <TransferVotedPanel />
          </ShowWhenTrue>
        </>
      )}
      <div className="flex justify-end">
        <Button variant={"primary"} disabled={!isValidAddress} onClick={onTransfer}>
          Transfer
        </Button>
      </div>
    </Card.Container>
  )
}
